import {
  AI_MODELS,
  AI_PROVIDERS,
  CUSTOM_AI_MODEL,
  CUSTOM_AI_PROVIDER,
  VISIBLE_AI_MODELS,
  VISIBLE_AI_PROVIDERS,
} from '../constants';
import { getAuthUser } from '../services/auth';
import { useChatCatalogStore } from '../stores/chatCatalogStore';
import { getItem, setItem } from './scopedSettingsStorage';

// ---- The provider/model catalog ----
//
// The admin console publishes which providers and models the composer offers
// (stores/chatCatalogStore). Non-admins only see pinned options until visibility
// has loaded; the built-in list is used when the catalog is explicitly unconfigured.
// RealPLC (AutoFree) and the custom provider are not
// catalog rows: the composer pins them first and last itself.

export interface CatalogProviderOption {
  value: string;
  label: string;
  baseUrl: string;
  /** Only present for rows from the catalog; false marks a row hidden from users. */
  visible?: boolean;
}

export interface CatalogModelOption {
  value: string;
  label: string;
  provider: string;
  visible?: boolean;
}

const REALPLC_PROVIDER: CatalogProviderOption = AI_PROVIDERS.find((item) => item.value === 'realplc')!;
const CUSTOM_PROVIDER: CatalogProviderOption = AI_PROVIDERS.find((item) => item.value === CUSTOM_AI_PROVIDER)!;
const AUTOFREE_MODEL: CatalogModelOption = AI_MODELS.find((item) => item.provider === 'realplc')!;
const CUSTOM_MODEL: CatalogModelOption = AI_MODELS.find((item) => item.provider === CUSTOM_AI_PROVIDER)!;

function loadedCatalog() {
  const { catalog } = useChatCatalogStore.getState();
  return catalog?.configured ? catalog : null;
}

/**
 * Filter again on the client: the shared cache may contain a super admin's
 * hidden rows after the signed-in user or their role changes.
 */
export function getCatalogProviders(options: { isSuperAdmin?: boolean } = {}): CatalogProviderOption[] {
  const isSuperAdmin = options.isSuperAdmin ?? getAuthUser()?.role === 'super_admin';
  const catalog = loadedCatalog();
  if (!catalog) {
    if (isSuperAdmin) return AI_PROVIDERS;
    return useChatCatalogStore.getState().catalog ? VISIBLE_AI_PROVIDERS : [REALPLC_PROVIDER, CUSTOM_PROVIDER];
  }
  return [
    REALPLC_PROVIDER,
    ...catalog.providers
      .filter((p) => isSuperAdmin || p.visible)
      .map((p) => ({ value: p.provider, label: p.label, baseUrl: p.baseUrl || '', visible: p.visible })),
    CUSTOM_PROVIDER,
  ];
}

export function getCatalogModels(options: { isSuperAdmin?: boolean } = {}): CatalogModelOption[] {
  const isSuperAdmin = options.isSuperAdmin ?? getAuthUser()?.role === 'super_admin';
  const catalog = loadedCatalog();
  if (!catalog) {
    if (isSuperAdmin) return AI_MODELS;
    return useChatCatalogStore.getState().catalog ? VISIBLE_AI_MODELS : [AUTOFREE_MODEL, CUSTOM_MODEL];
  }
  const visibleProviders = new Set(catalog.providers.filter((p) => p.visible).map((p) => p.provider));
  return [
    AUTOFREE_MODEL,
    ...catalog.models
      .filter((m) => isSuperAdmin || (m.visible && visibleProviders.has(m.provider)))
      .map((m) => ({ value: m.model, label: m.label || m.model, provider: m.provider, visible: m.visible })),
    CUSTOM_MODEL,
  ];
}

/**
 * Constants ∪ catalog by slug, the catalog winning. Only for reading and
 * writing credentials: a key the user saved must be found whether or not its
 * provider is on offer today, and a provider added in the admin console needs
 * its base URL to reach the runtime through the credential maps.
 */
export function getKnownProviders(): CatalogProviderOption[] {
  const bySlug = new Map<string, CatalogProviderOption>();
  for (const item of AI_PROVIDERS) bySlug.set(item.value, item);
  for (const p of loadedCatalog()?.providers ?? []) {
    bySlug.set(p.provider, { value: p.provider, label: p.label, baseUrl: p.baseUrl || '' });
  }
  return [...bySlug.values()];
}

export function normalizeProvider(provider?: string | null): string {
  if (!provider) return '';
  const normalized = provider === 'qwen' ? 'aliyun' : provider === 'shengong' ? 'realplc' : provider;
  return getCatalogProviders({ isSuperAdmin: true }).some((item) => item.value === normalized)
    ? normalized as string
    : '';
}

// ---- Custom Model Entry (multi-entry support) ----

export interface CustomModelEntry {
  id: string;
  providerName: string;
  modelId: string;
  apiKey: string;
  baseUrl: string;
}

const CUSTOM_MODEL_ENTRIES_KEY = 'custom_model_entries';
const CUSTOM_MODEL_MIGRATED_KEY = 'custom_model_migrated';
const CUSTOM_MODEL_CONNECTIONS_KEY = 'custom_model_connections';
const CUSTOM_MODEL_SELECTION_ID_KEY = 'custom_model_selection_id';

function generateEntryId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = 'cm_';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export { generateEntryId };

function migrateLegacyConfig(): void {
  if (typeof localStorage === 'undefined') return;
  if (getItem(CUSTOM_MODEL_MIGRATED_KEY) === 'true') return;
  const oldModelId = getItem('custom_model_id')?.trim();
  if (!oldModelId) {
    setItem(CUSTOM_MODEL_MIGRATED_KEY, 'true');
    return;
  }
  const entry: CustomModelEntry = {
    id: generateEntryId(),
    providerName: getItem('custom_model_provider_label')?.trim() || '',
    modelId: oldModelId,
    apiKey: getItem('custom_api_key') || '',
    baseUrl: getItem('custom_base_url') || '',
  };
  setItem(CUSTOM_MODEL_ENTRIES_KEY, JSON.stringify([entry]));
  const currentProvider = getItem('ai_provider');
  const currentModel = getItem('ai_model');
  if (currentProvider === 'custom' && currentModel === oldModelId) {
    setItem('ai_model', entry.id);
  }
  setItem(CUSTOM_MODEL_MIGRATED_KEY, 'true');
}

export function getCustomModelEntries(): CustomModelEntry[] {
  if (typeof localStorage === 'undefined') return [];
  migrateLegacyConfig();
  try {
    const raw = getItem(CUSTOM_MODEL_ENTRIES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is CustomModelEntry =>
      typeof item === 'object' && item !== null && typeof item.id === 'string'
    );
  } catch {
    return [];
  }
}

function findCustomConnectionModel(
  connections: CustomModelConnection[],
  candidate?: string | null,
): { connection: CustomModelConnection; model: CustomProviderModel } | undefined {
  const candidates = [
    candidate,
    getItem('ai_model'),
    getItem(CUSTOM_MODEL_SELECTION_ID_KEY),
  ].map((item) => item?.trim()).filter((item): item is string => Boolean(item));

  for (const value of candidates) {
    if (value === CUSTOM_AI_MODEL) continue;
    for (const connection of connections) {
      const model = connection.models.find((item) => item.id === value);
      if (model) return { connection, model };
    }
    for (const connection of connections) {
      const model = connection.models.find((item) => item.modelId.trim() === value);
      if (model) return { connection, model };
    }
  }

  for (const connection of connections) {
    const model = connection.models.find((item) => item.modelId.trim());
    if (model) return { connection, model };
  }

  return undefined;
}

export function getActiveCustomModelSelection(
  fallbackModel?: string | null,
): { connection: CustomModelConnection; model: CustomProviderModel } | undefined {
  return findCustomConnectionModel(getCustomModelConnections(), fallbackModel);
}

export function saveCustomModelEntries(entries: CustomModelEntry[]): void {
  if (typeof localStorage === 'undefined') return;
  setItem(CUSTOM_MODEL_ENTRIES_KEY, JSON.stringify(entries));
}

export function getActiveCustomModelEntry(): CustomModelEntry | undefined {
  if (typeof localStorage === 'undefined') return undefined;
  const provider = getItem('ai_provider');
  if (provider !== 'custom') return undefined;
  const activeSelection = getActiveCustomModelSelection();
  if (activeSelection) {
    return {
      id: activeSelection.model.id,
      providerName: activeSelection.connection.name,
      modelId: activeSelection.model.modelId,
      apiKey: activeSelection.connection.apiKey,
      baseUrl: activeSelection.connection.baseUrl,
    };
  }
  const activeModel = getItem('ai_model');
  if (!activeModel) return undefined;
  const entries = getCustomModelEntries();
  return entries.find((e) => e.id === activeModel || e.modelId === activeModel);
}

// ---- Legacy accessors (fall back to entry data) ----

export function getCustomModelId(): string {
  if (typeof localStorage === 'undefined') return '';
  const activeEntry = getActiveCustomModelEntry();
  if (activeEntry) return activeEntry.modelId;
  return getItem('custom_model_id')?.trim() || '';
}

export function getCustomModelLabel(): string {
  if (typeof localStorage === 'undefined') return '';
  const activeEntry = getActiveCustomModelEntry();
  if (activeEntry) return activeEntry.providerName
    ? `${activeEntry.providerName} - ${activeEntry.modelId}`
    : activeEntry.modelId;
  return getItem('custom_model_label')?.trim() || '';
}

export function getCustomProviderLabel(): string {
  if (typeof localStorage === 'undefined') return '';
  const activeEntry = getActiveCustomModelEntry();
  if (activeEntry) return activeEntry.providerName || 'Custom Model';
  return getItem('custom_model_provider_label')?.trim() || '';
}

// ---- Provider name history ----

const PROVIDER_NAME_HISTORY_KEY = 'custom_model_provider_name_history';

export function getCustomProviderNameHistory(): string[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = getItem(PROVIDER_NAME_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
  } catch {
    return [];
  }
}

export function saveCustomProviderNameToHistory(name: string): void {
  if (typeof localStorage === 'undefined') return;
  const trimmed = name.trim();
  if (!trimmed) return;
  const history = getCustomProviderNameHistory();
  const updated = [trimmed, ...history.filter((item) => item !== trimmed)].slice(0, 20);
  setItem(PROVIDER_NAME_HISTORY_KEY, JSON.stringify(updated));
}

// ---- Model / provider resolution ----

export function resolveModelSelection(provider?: string | null, model?: string | null, options: { isSuperAdmin?: boolean } = {}) {
  const effectiveProvider = provider?.trim() || getItem('ai_provider') || '';
  const normalizedProvider = normalizeProvider(effectiveProvider) || REALPLC_PROVIDER.value;

  if (normalizedProvider === CUSTOM_AI_PROVIDER) {
    const activeSelection = getActiveCustomModelSelection(model);
    if (activeSelection?.model.modelId.trim()) {
      return { provider: CUSTOM_AI_PROVIDER, model: activeSelection.model.modelId.trim() };
    }

    const entries = getCustomModelEntries();
    if (model) {
      const matchedEntry = entries.find((e) => e.id === model);
      if (matchedEntry) {
        return { provider: CUSTOM_AI_PROVIDER, model: matchedEntry.modelId };
      }
    }
    const customModel = getCustomModelId() || model?.trim() || CUSTOM_AI_MODEL;
    return { provider: CUSTOM_AI_PROVIDER, model: customModel };
  }

  const models = getCatalogModels(options);
  const providerModels = models.filter((item) => item.provider === normalizedProvider);
  const effectiveModel = model?.trim() || getItem('ai_model') || '';
  const matchedModel = providerModels.find((item) => item.value === effectiveModel)
    || providerModels[0]
    || models[0];

  return {
    provider: matchedModel.provider,
    model: matchedModel.value,
  };
}

export function getProviderDisplayLabel(provider: string): string {
  if (provider === CUSTOM_AI_PROVIDER) {
    return getCustomProviderLabel() || CUSTOM_PROVIDER.label || provider;
  }

  return getCatalogProviders({ isSuperAdmin: true }).find((item) => item.value === provider)?.label || provider;
}

export function getModelDisplayLabel(provider: string, model: string, options: { isSuperAdmin?: boolean } = {}): string {
  if (provider === CUSTOM_AI_PROVIDER) {
    return getCustomModelLabel() || model || 'Custom Model';
  }

  const models = getCatalogModels(options);
  return models.find((item) => item.provider === provider && item.value === model)?.label
    || models.find((item) => item.value === model)?.label
    || model;
}

export function buildProviderCredentialMaps() {
  const knownProviders = getKnownProviders();
  const providerApiKeys = Object.fromEntries(
    knownProviders.map(({ value }) => [
      value,
      getItem(`${value}_api_key`)
        || (value === 'aliyun' ? getItem('qwen_api_key') : null)
        || '',
    ])
  );

  const providerBaseUrls = Object.fromEntries(
    knownProviders.map(({ value, baseUrl }) => [
      value,
      getItem(`${value}_base_url`)
        || (value === 'aliyun' ? getItem('qwen_base_url') : null)
        || baseUrl
        || '',
    ])
  );

  const activeEntry = getActiveCustomModelEntry();
  if (activeEntry) {
    if (activeEntry.apiKey) providerApiKeys['custom'] = activeEntry.apiKey;
    if (activeEntry.baseUrl) providerBaseUrls['custom'] = activeEntry.baseUrl;
  }

  return { providerApiKeys, providerBaseUrls };
}

/**
 * Whether the selector should still offer this provider.
 *
 * Two independent sources make a provider runnable, and only one of them is
 * visible from here: the key the user pasted into settings, and the key the
 * platform holds. `platformProviders` carries the second — null when the lookup
 * has not answered, in which case everything is offered. Hiding a provider
 * because a status call failed would be a worse bug than the one this prevents.
 *
 * AutoFree filters its own pool by credential on every turn, and a custom
 * connection is nothing but the user's own key, so neither depends on the
 * platform's answer.
 */
export function isProviderSelectable(
  provider: string,
  platformProviders: Record<string, boolean> | null,
): boolean {
  const slug = String(provider || '').trim().toLowerCase();
  if (!slug || slug === 'realplc' || slug === CUSTOM_AI_PROVIDER) return true;
  if (!platformProviders) return true;
  if (platformProviders[slug]) return true;
  return Boolean(buildProviderCredentialMaps().providerApiKeys[slug]);
}

/**
 * Credentials come from storage at send time, never from the config being sent.
 *
 * Only the input box ever built a config with credentials in it. Everything that
 * replays one — retry, regenerate, a clarify answer, a session reopened after a
 * refresh — carries `preferredProvider` forward while `sessionGenerationConfig`
 * rebuilds the config without any credential fields at all. The result was a
 * turn that asked for a provider and brought no key for it, and since a manually
 * chosen model has no fallback candidates, the turn died on a key the user had
 * already entered. Re-reading here means a key added mid-session takes effect on
 * the next send instead of the next full page load.
 */
export function withCurrentProviderCredentials<T extends object>(config: T): T & {
  providerApiKeys: Record<string, string>;
  providerBaseUrls: Record<string, string>;
} {
  const { providerApiKeys, providerBaseUrls } = buildProviderCredentialMaps();
  return { ...config, providerApiKeys, providerBaseUrls };
}

// ---- Nested Custom Model Connection (connection → models) ----

export interface CustomProviderModel {
  id: string;
  modelId: string;
  displayName: string;
  enabled: boolean;
  source: 'manual' | 'fetched';
}

export interface CustomModelConnection {
  id: string;
  name: string;
  apiKey: string;
  baseUrl: string;
  enabled: boolean;
  models: CustomProviderModel[];
}

function generateConnectionId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = 'conn_';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function createBlankCustomModel(): CustomProviderModel {
  return {
    id: generateEntryId(),
    modelId: '',
    displayName: '',
    enabled: true,
    source: 'manual',
  };
}

export function createBlankCustomConnection(): CustomModelConnection {
  return {
    id: generateConnectionId(),
    name: '',
    apiKey: '',
    baseUrl: '',
    enabled: true,
    models: [createBlankCustomModel()],
  };
}

export function getCustomModelConnections(): CustomModelConnection[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = getItem(CUSTOM_MODEL_CONNECTIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item: any) =>
      typeof item === 'object' && item !== null && typeof item.id === 'string' && Array.isArray(item.models)
    );
  } catch {
    return [];
  }
}

export function saveCustomModelConnections(connections: CustomModelConnection[]): void {
  if (typeof localStorage === 'undefined') return;
  setItem(CUSTOM_MODEL_CONNECTIONS_KEY, JSON.stringify(connections));
}

export function saveCustomModelSelectionId(modelId: string): void {
  if (typeof localStorage === 'undefined') return;
  setItem(CUSTOM_MODEL_SELECTION_ID_KEY, modelId);
}

export function getCustomModelSelectionId(fallbackModel?: string): string {
  if (typeof localStorage === 'undefined') return fallbackModel || '';
  const activeSelection = getActiveCustomModelSelection(fallbackModel);
  return activeSelection?.model.id || fallbackModel || '';
}

export function getSelectedProviderRuntimeConfig(provider: string) {
  const providerObj = getKnownProviders().find((item) => item.value === provider);

  let apiKey: string | undefined;
  let baseUrl: string | undefined;

  if (provider === CUSTOM_AI_PROVIDER) {
    const activeEntry = getActiveCustomModelEntry();
    apiKey = activeEntry?.apiKey || undefined;
    baseUrl = activeEntry?.baseUrl || undefined;
  }

  if (!apiKey) {
    apiKey = getItem(`${provider}_api_key`)
      || (provider === 'aliyun' ? getItem('qwen_api_key') : null)
      || undefined;
  }
  if (!baseUrl) {
    baseUrl = getItem(`${provider}_base_url`)
      || (provider === 'aliyun' ? getItem('qwen_base_url') : null)
      || providerObj?.baseUrl
      || undefined;
  }

  return { apiKey, baseUrl };
}
